import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { agencyAPI, settingsAPI } from '../api';
import TourTicket from './TourTicket';
import Money from '../components/Money';
import { IconTrash } from '@tabler/icons-react';

const fmt = (n, cur='TRY') => (cur==='TRY'?'₺':cur+' ') + parseFloat(n||0).toLocaleString('tr-TR',{minimumFractionDigits:2});
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

const SALE_TYPES = [
  { value:'tour', label:'Günlük Tur', cls:'badge-cat-1' },
  { value:'hotel', label:'Otel', cls:'badge-cat-2' },
  { value:'apartment', label:'Daire', cls:'badge-cat-5' },
  { value:'flight', label:'Uçak', cls:'badge-cat-4' },
  { value:'exchange', label:'Döviz', cls:'badge-cat-3' },
];

const emptyForm = {
  sale_type:'tour', description:'', tour_name:'', tour_date:'', tour_time:'', tour_stops:[],
  property_name:'', room_number:'', guest_name:'', check_in_date:'', check_out_date:'',
  daily_price:'', daily_price_currency:'TRY', guest_name_tour:'', pax_count:1,
  phone:'', tour_hotel:'', tour_room:'', deposit_amount:'', deposit_currency:'TRY',
  payment_channel:'Nakit', sale_amount:'', sale_currency:'TRY',
  cost_amount:'', cost_currency:'TRY', buy_rate:'', sale_date: new Date().toISOString().slice(0,10), notes:''
};

export default function Agency() {
  const { currentFirm } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0,7));
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(''),3000); };

  const load = useCallback(async () => {
    if (!currentFirm) return;
    const params = {};
    if (filterType !== 'all') params.sale_type = filterType;
    if (filterMonth) params.month = filterMonth;
    const [salesRes, sumRes, curRes] = await Promise.all([
      agencyAPI.getAll(currentFirm.id, params),
      agencyAPI.getSummary(currentFirm.id, params),
      settingsAPI.getCurrencies(currentFirm.id),
    ]);
    // API {sales, total} nesnesi döndürüyor; dizi dönen sürümlerle de uyumlu kalsın.
    setSales(Array.isArray(salesRes.data) ? salesRes.data : (salesRes.data?.sales || []));
    setSummary(sumRes.data);
    setCurrencies(Array.isArray(curRes.data) ? curRes.data : []);
  }, [currentFirm, filterType, filterMonth]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({
      sale_type:s.sale_type, description:s.description||'', tour_name:s.tour_name||'', tour_date:s.tour_date?.slice(0,10)||'',
      tour_time:s.tour_time||'', tour_stops:s.tour_route?(() => { try { return JSON.parse(s.tour_route); } catch { return s.tour_route.split(',').map(x=>x.trim()); } })():[],
      property_name:s.property_name||'', room_number:s.room_number||'', guest_name:s.guest_name||'',
      check_in_date:s.check_in_date?.slice(0,10)||'', check_out_date:s.check_out_date?.slice(0,10)||'',
      daily_price:s.daily_price||'', daily_price_currency:s.daily_price_currency||'TRY',
      guest_name_tour:s.guest_name_tour||'', pax_count:s.pax_count||1,
      phone:s.phone||'', tour_hotel:s.tour_hotel||'', tour_room:s.tour_room||'',
      deposit_amount:s.deposit_amount||'', deposit_currency:s.deposit_currency||'TRY',
      payment_channel:s.payment_channel||'Nakit', sale_amount:s.sale_amount||'',
      sale_currency:s.sale_currency||'TRY', cost_amount:s.cost_amount||'',
      cost_currency:s.cost_currency||'TRY', buy_rate:s.buy_rate||'',
      sale_date:s.sale_date?.slice(0,10)||new Date().toISOString().slice(0,10), notes:s.notes||''
    });
    setEditId(s.id); setShowForm(true);
  };

  const saveSale = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...form, tour_route: JSON.stringify(form.tour_stops||[]) };
      if (editId) { await agencyAPI.update(currentFirm.id, editId, data); setEditId(null); }
      else await agencyAPI.create(currentFirm.id, data);
      setShowForm(false); load(); flash('Satış kaydedildi.');
    } catch(err) { flash(err.response?.data?.error||'Hata'); }
    finally { setSaving(false); }
  };

  const deleteSale = async (id) => {
    if (!window.confirm('Satışı silmek istiyor musunuz?')) return;
    await agencyAPI.delete(currentFirm.id, id);
    load(); flash('Satış silindi.');
  };

  const openTicket = (s) => {
    const firmData = { id: currentFirm.id, name: currentFirm.name };
    setTicketData({ sale: s, firm: firmData });
  };

  const addStop = () => setForm(f => ({ ...f, tour_stops:[...(f.tour_stops||[]),''] }));
  const updateStop = (i,v) => { const s=[...(form.tour_stops||[])]; s[i]=v; setForm(f=>({...f,tour_stops:s})); };
  const removeStop = (i) => setForm(f=>({...f,tour_stops:(f.tour_stops||[]).filter((_,j)=>j!==i)}));

  const inp = { height:34, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, background:'var(--bg2)', color:'var(--text)', width:'100%' };
  const lbl = { fontSize:11, color:'var(--text2)', marginBottom:4, fontWeight:500 };
  const isTour = form.sale_type === 'tour';
  const isProperty = form.sale_type === 'hotel' || form.sale_type === 'apartment';
  const isExchange = form.sale_type === 'exchange';

  if (!currentFirm) return <div style={{padding:'2rem',color:'var(--text3)',textAlign:'center'}}>Firma seçin.</div>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {msg && <div style={{padding:'8px 14px',borderRadius:'var(--radius)',background:'var(--green-bg)',color:'var(--green)',border:'1px solid var(--green-border)',fontSize:13}}>✓ {msg}</div>}

      {/* Özet — backend değerleri summary.totals içinde döndürüyor,
          kârı da kendisi hesaplıyor; burada yeniden hesaplamıyoruz. */}
      {summary && (
        <div className="metric-grid" style={{gridTemplateColumns:'repeat(4,1fr)'}}>
          {(() => {
            const tt = summary.totals || {};
            const profit = Number(tt.profit_try || 0);
            return [
              {label:'Toplam satış', value:Number(tt.total_try||0), tone:'pos'},
              {label:'Toplam maliyet', value:Number(tt.cost_try||0), tone:'neg'},
              {label:'Kâr', value:profit, tone:profit>=0?'pos':'neg'},
              {label:'İşlem', count:Number(tt.count||0)},
            ];
          })().map(m=>(
            <div className="metric" key={m.label}>
              <div className="metric-head"><span className="metric-label">{m.label}</span></div>
              {m.count!==undefined
                ? <span className="figure figure-lg">{m.count.toLocaleString('tr-TR')}</span>
                : <Money value={m.value} tone={m.tone} size="lg"/>}
              <span className="balance-rule" style={{color:m.tone?`var(--${m.tone})`:'var(--fg-3)'}}/>
            </div>
          ))}
        </div>
      )}

      {/* Filtre + Ekle */}
      <div className="card" style={{padding:'12px 16px'}}>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:4}}>
            {[{value:'all',label:'Tümü'},...SALE_TYPES].map(t=>(
              <button key={t.value} type="button" onClick={()=>setFilterType(t.value)}
                className={`chip${filterType===t.value?' active':''}`}>{t.label}</button>
            ))}
          </div>
          <input type="month" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{height:30,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:145}}/>
          <div style={{marginLeft:'auto',display:'flex',gap:6}}>
            <button onClick={()=>navigate('/acente/paket-turlar')} className="btn btn-ghost btn-sm">Paket Turlar</button>
            <button onClick={openNew} className="btn btn-dark btn-sm">+ Satış Ekle</button>
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card">
          <div style={{fontSize:14,fontWeight:600,marginBottom:16}}>{editId?'Satış Düzenle':'Yeni Satış'}</div>
          <form onSubmit={saveSale}>
            {/* Tür seçimi */}
            <div style={{display:'flex',gap:6,marginBottom:14}}>
              {SALE_TYPES.map(t=>(
                <button key={t.value} type="button" onClick={()=>setForm(f=>({...f,sale_type:t.value}))}
                  className={`chip${form.sale_type===t.value?' active':''}`} style={{height:32}}>
                  {t.label}
                </button>
              ))}
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
              <div><div style={lbl}>Satış Tarihi *</div><input type="date" value={form.sale_date} onChange={e=>setForm(f=>({...f,sale_date:e.target.value}))} style={inp} required/></div>
              <div><div style={lbl}>Açıklama</div><input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={inp}/></div>
              <div><div style={lbl}>Ödeme Kanalı</div>
                <select value={form.payment_channel} onChange={e=>setForm(f=>({...f,payment_channel:e.target.value}))} style={inp}>
                  {['Nakit','Banka Havalesi','Kredi Kartı','EFT','Çek','Diğer'].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            {/* Tur alanları */}
            {isTour && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
                <div><div style={lbl}>Tur Adı</div><input value={form.tour_name} onChange={e=>setForm(f=>({...f,tour_name:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Tur Tarihi</div><input type="date" value={form.tour_date} onChange={e=>setForm(f=>({...f,tour_date:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Kalkış Saati</div><input type="time" value={form.tour_time} onChange={e=>setForm(f=>({...f,tour_time:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Misafir Adı</div><input value={form.guest_name_tour} onChange={e=>setForm(f=>({...f,guest_name_tour:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Kişi Sayısı</div><input type="number" value={form.pax_count} onChange={e=>setForm(f=>({...f,pax_count:e.target.value}))} style={inp} min="1"/></div>
                <div><div style={lbl}>Telefon</div><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Otel / Konaklama</div><input value={form.tour_hotel} onChange={e=>setForm(f=>({...f,tour_hotel:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Oda No</div><input value={form.tour_room} onChange={e=>setForm(f=>({...f,tour_room:e.target.value}))} style={inp}/></div>
                <div>
                  <div style={lbl}>Duraklar</div>
                  <div style={{display:'flex',flexDirection:'column',gap:4}}>
                    {(form.tour_stops||[]).map((stop,i)=>(
                      <div key={i} style={{display:'flex',gap:4,alignItems:'center'}}>
                        <span style={{fontSize:11,color:'var(--text3)',minWidth:16}}>{i+1}.</span>
                        <input value={stop} onChange={e=>updateStop(i,e.target.value)} style={{...inp,height:28,fontSize:12}} placeholder="Durak adı"/>
                        <button type="button" onClick={()=>removeStop(i)} style={{width:24,height:24,border:'1px solid var(--red-border)',borderRadius:'var(--radius)',background:'var(--red-bg)',color:'var(--red)',cursor:'pointer',fontSize:13}}>×</button>
                      </div>
                    ))}
                    <button type="button" onClick={addStop} style={{height:28,padding:'0 10px',border:'1px dashed var(--border)',borderRadius:'var(--radius)',background:'none',cursor:'pointer',fontSize:12,color:'var(--text3)',textAlign:'left'}}>+ Durak Ekle</button>
                  </div>
                </div>
              </div>
            )}

            {/* Otel/Daire alanları */}
            {isProperty && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
                <div><div style={lbl}>{form.sale_type==='hotel'?'Otel Adı':'Daire'}</div><input value={form.property_name} onChange={e=>setForm(f=>({...f,property_name:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Oda / Daire No</div><input value={form.room_number} onChange={e=>setForm(f=>({...f,room_number:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Misafir Adı</div><input value={form.guest_name} onChange={e=>setForm(f=>({...f,guest_name:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Telefon</div><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Giriş Tarihi</div><input type="date" value={form.check_in_date} onChange={e=>setForm(f=>({...f,check_in_date:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Çıkış Tarihi</div><input type="date" value={form.check_out_date} onChange={e=>setForm(f=>({...f,check_out_date:e.target.value}))} style={inp}/></div>
                <div><div style={lbl}>Günlük Fiyat</div><input type="number" value={form.daily_price} onChange={e=>setForm(f=>({...f,daily_price:e.target.value}))} style={inp} min="0" step="0.01"/></div>
                <div><div style={lbl}>Döviz</div><select value={form.daily_price_currency} onChange={e=>setForm(f=>({...f,daily_price_currency:e.target.value}))} style={inp}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
              </div>
            )}

            {/* Döviz alanları */}
            {isExchange && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
                <div><div style={lbl}>Alış Kuru</div><input type="number" value={form.buy_rate} onChange={e=>setForm(f=>({...f,buy_rate:e.target.value}))} style={inp} min="0" step="0.0001"/></div>
              </div>
            )}

            {/* Satış / Maliyet */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
              <div><div style={lbl}>Satış Tutarı *</div><input type="number" value={form.sale_amount} onChange={e=>setForm(f=>({...f,sale_amount:e.target.value}))} style={inp} required min="0" step="0.01"/></div>
              <div><div style={lbl}>Satış Dövizi</div><select value={form.sale_currency} onChange={e=>setForm(f=>({...f,sale_currency:e.target.value}))} style={inp}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
              <div><div style={lbl}>Kapora</div><input type="number" value={form.deposit_amount} onChange={e=>setForm(f=>({...f,deposit_amount:e.target.value}))} style={inp} min="0" step="0.01"/></div>
              <div><div style={lbl}>Maliyet</div><input type="number" value={form.cost_amount} onChange={e=>setForm(f=>({...f,cost_amount:e.target.value}))} style={inp} min="0" step="0.01"/></div>
              <div><div style={lbl}>Maliyet Dövizi</div><select value={form.cost_currency} onChange={e=>setForm(f=>({...f,cost_currency:e.target.value}))} style={inp}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
              <div><div style={lbl}>Not</div><input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inp}/></div>
            </div>

            <div style={{display:'flex',gap:8}}>
              <button type="submit" disabled={saving} className="btn btn-dark">{saving?'Kaydediliyor...':'Kaydet'}</button>
              <button type="button" onClick={()=>setShowForm(false)} className="btn btn-ghost">İptal</button>
            </div>
          </form>
        </div>
      )}

      {/* Satış Listesi */}
      <div className="card">
        <div style={{fontSize:14,fontWeight:600,marginBottom:12}}>{sales.length} Satış</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{borderBottom:'1px solid var(--border)'}}>
              {['Tür','Tarih','Açıklama','Satış','Kapora','Kâr',''].map(h=><th key={h} style={{padding:'7px 8px',fontSize:10,color:'var(--text3)',fontWeight:500,textAlign:'left',textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {sales.map(s => {
                const stype = SALE_TYPES.find(t=>t.value===s.sale_type)||SALE_TYPES[0];
                return (
                  <tr key={s.id}>
                    <td><span className={`badge ${stype.cls}`}>{stype.label}</span></td>
                    <td style={{color:'var(--fg-3)',whiteSpace:'nowrap'}}>{fmtDate(s.sale_date)}</td>
                    <td style={{fontWeight:550,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {s.guest_name_tour||s.guest_name||s.tour_name||s.property_name||s.description||'—'}
                    </td>
                    <td className="num"><Money value={s.sale_amount_try} tone="pos" size="sm"/></td>
                    <td className="num" style={{color:'var(--fg-3)'}}>
                      {parseFloat(s.deposit_amount||0)>0
                        ? <Money value={s.deposit_amount_try||s.deposit_amount} size="sm"/>
                        : '—'}
                    </td>
                    <td className="num">
                      <Money value={s.profit_try} tone={parseFloat(s.profit_try||0)>=0?'pos':'neg'} size="sm"/>
                    </td>
                    <td>
                      <div style={{display:'flex',gap:5,justifyContent:'flex-end'}}>
                        <button type="button" onClick={()=>openTicket(s)} className="btn btn-ghost btn-sm">Bilet</button>
                        <button type="button" onClick={()=>openEdit(s)} className="btn btn-ghost btn-sm">Düzenle</button>
                        <button type="button" onClick={()=>deleteSale(s.id)} className="btn btn-sm btn-icon" aria-label="Satışı sil"
                          style={{background:'var(--neg-tint)',borderColor:'var(--neg-line)',color:'var(--neg)'}}>
                          <IconTrash size={14} strokeWidth={1.7}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sales.length===0&&<tr><td colSpan={7} style={{padding:'2rem',textAlign:'center',color:'var(--text3)'}}>Satış kaydı yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {ticketData && <TourTicket data={ticketData} onClose={()=>setTicketData(null)}/>}
    </div>
  );
}
